// src/components/SideGraph.jsx
import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "./SideGraph.css";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, ChartDataLabels);

// --- IST helpers ---
const IST_TZ = "Asia/Kolkata";

const fmtDMY = (d) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: IST_TZ,
  }); // [web:8]

const monthKey = (d) =>
  new Date(d).toLocaleString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: IST_TZ,
  }); // [web:8]

const getISTWeekday = (d) =>
  new Date(d).toLocaleDateString("en-GB", { weekday: "long", timeZone: IST_TZ }); // [web:8]

const isSundayIST = (d) => getISTWeekday(d) === "Sunday"; // [web:8]
const isSaturdayIST = (d) => getISTWeekday(d) === "Saturday"; // [web:8]

const parseDMY = (s) => {
  const str = String(s).trim();
  if (/^\d{2}\s\w{3}\s\d{4}$/.test(str)) {
    const [dd, mon, yyyy] = str.split(" ");
    return new Date(`${dd} ${mon} ${yyyy} 00:00:00 GMT+0530`);
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}; // [web:8]

const parseIST12hToDate = (dateRef, str) => {
  const d = new Date(dateRef);
  const [time, meridianRaw] = String(str).split(" ");
  const [h, m, s] = (time || "").split(":");
  let hour = parseInt(h || "0", 10);
  const meridian = (meridianRaw || "").toLowerCase();
  if (meridian === "pm" && hour !== 12) hour += 12;
  if (meridian === "am" && hour === 12) hour = 0;
  d.setHours(hour, parseInt(m || "0", 10), parseInt(s || "0", 10), 0);
  return d;
}; // [web:8]

const diffHours = (start, end) => Math.max(0, (end - start) / 36e5); // [web:8]

// Month helpers
const addMonths = (d, n) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}; // [web:8]

const firstOfMonthIST = (d) => {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}; // [web:8]

const lastOfMonthIST = (d) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + 1, 0);
  x.setHours(0, 0, 0, 0);
  return x;
}; // [web:8]

// Last two months + current
const last2AndCurrent = (now) => {
  const cur = new Date(now);
  const prev1 = addMonths(cur, -1);
  const prev2 = addMonths(cur, -2);
  return [prev2, prev1, cur];
}; // [web:8]

// Compare calendar day (IST)
const sameDayIST = (a, b) =>
  new Date(a).toLocaleDateString("en-GB", { timeZone: IST_TZ }) ===
  new Date(b).toLocaleDateString("en-GB", { timeZone: IST_TZ }); // [web:8]

// Generate each day inclusive (IST)
const eachDayIST = (start, end) => {
  const days = [];
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(0, 0, 0, 0);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }
  return days;
}; // [web:8]

export default function SideGraph({ attendanceRecords, employeeName }) {
  const now = new Date();
  const [m2, m1, curM] = last2AndCurrent(now);
  const labels = [monthKey(m2), monthKey(m1), monthKey(curM)]; // chart stays 3 months [web:11]

  const {
    monthTotals,
    dailyStatuses,
    // badges only for current month
    totalsCurrent: { checkoutCount, shortfallCount, missingCheckoutCount, noShowCount },
  } = useMemo(() => {
    // Merge raw input by date key
    const rawByDate = new Map();
    (attendanceRecords || []).forEach((r) => {
      const d = parseDMY(r.date);
      if (!d) return;
      const key = fmtDMY(d);
      const prev = rawByDate.get(key) || { date: d, checkIn: null, checkOut: null };
      if (r.checkIn) prev.checkIn = r.checkIn;
      if (r.checkOut) prev.checkOut = r.checkOut;
      rawByDate.set(key, prev);
    }); // [web:8]

    // Build working-day calendar from first of m2 up to today (not beyond today)
    const start = firstOfMonthIST(m2);
    const endOfCurrentMonth = lastOfMonthIST(curM);
    const todayOnly = new Date(now);
    todayOnly.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(Math.min(endOfCurrentMonth.getTime(), todayOnly.getTime())); // [web:8]

    const allDays = eachDayIST(start, rangeEnd).filter((d) => !isSundayIST(d)); // [web:8]

    // Placeholders for 3‑month window
    const dayMap = new Map();
    allDays.forEach((d) => {
      dayMap.set(fmtDMY(d), {
        date: d,
        totalHours: 0,
        status: "Absent",
        isCheckedOut: false,
        isMissingOut: false,
        isPendingToday: false,
        isNoShow: false,
      });
    }); // [web:8]

    // Merge attendance into placeholders
    rawByDate.forEach((rec, key) => {
      const d = rec.date;
      if (isSundayIST(d)) return;
      if (!dayMap.has(key)) return;

      const day = dayMap.get(key);
      const hasIn = !!rec.checkIn;
      const hasOut = !!rec.checkOut;

      if (hasIn && hasOut) {
        const hrs = diffHours(
          parseIST12hToDate(d, rec.checkIn),
          parseIST12hToDate(d, rec.checkOut)
        );
        day.totalHours = Math.max(day.totalHours, hrs);
        day.status = "Present";
        day.isCheckedOut = true;
      } else if (hasIn && !hasOut) {
        if (sameDayIST(d, now)) {
          day.status = "Pending";
          day.isPendingToday = true;
        } else {
          day.status = "Missing checkout";
          day.isMissingOut = true;
        }
      }
      dayMap.set(key, day);
    }); // [web:8]

    // Assign No‑show for past working days with no record (never future)
    dayMap.forEach((day, key) => {
      const recorded = rawByDate.has(key);
      const isToday = sameDayIST(day.date, now);
      if (!recorded && !isToday) {
        day.status = "No-show";
        day.isNoShow = true;
        day.totalHours = 0;
        dayMap.set(key, day);
      }
    }); // [web:8]

    // Aggregate monthTotals across 3 months for the bar chart
    const monthTotals = new Map();
    const pushMonth = (d, hours) => {
      const mk = monthKey(d);
      monthTotals.set(mk, (monthTotals.get(mk) || 0) + hours);
    }; // [web:11]

    // Badges: only current month counters
    let checkoutCount = 0;
    let shortfallCount = 0;
    let missingCheckoutCount = 0;
    let noShowCount = 0;

    dayMap.forEach((day) => {
      // For chart bars: always add checked-out hours to the month of that day
      if (day.isCheckedOut) {
        pushMonth(day.date, day.totalHours);
      }

      // For badges: count only current month days
      if (monthKey(day.date) !== monthKey(curM)) return;

      const target = isSaturdayIST(day.date) ? 6 : 9;

      if (day.isNoShow) {
        noShowCount += 1;
        return;
      }
      if (day.isMissingOut) {
        missingCheckoutCount += 1;
        return;
      }
      if (day.isPendingToday) {
        return;
      }
      if (day.isCheckedOut) {
        checkoutCount += 1;
        if (day.totalHours + 1e-9 < target) shortfallCount += 1;
      }
    }); // [web:11][web:8]

    // Daily list for current month only
    const curList = Array.from(dayMap.values())
      .filter((d) => monthKey(d.date) === monthKey(curM))
      .sort((a, b) => a.date - b.date); // [web:8]

    return {
      monthTotals,
      dailyStatuses: curList,
      totalsCurrent: { checkoutCount, shortfallCount, missingCheckoutCount, noShowCount },
    };
  }, [attendanceRecords]); // [web:11]

  // Chart with fixed 50h grid up to 250h
  const MAX_HOURS = 250;
  const STEP = 50;

  const data = {
    labels,
    datasets: [
      {
        label: "Total hours",
        data: labels.map((mk) => Number((monthTotals.get(mk) || 0).toFixed(2))),
        backgroundColor: ["#93c5fd", "#93c5fd", "#60a5fa"],
        borderColor: "#3b82f6",
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 36,
      },
    ],
  }; // [web:11]

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 16, right: 6, bottom: 0, left: 6 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#040000ff",
        anchor: "end",
        align: "top",
        offset: 4,
        clamp: true,
        clip: false,
        formatter: (v) => `${v}h`,
        font: { weight: "600", size: 10 },
      },
      tooltip: {
        callbacks: { label: (ctx) => ` ${ctx.parsed.y} h` },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkipPadding: 8 } },
      y: {
        beginAtZero: true,
        min: 0,
        max: MAX_HOURS,
        ticks: {
          stepSize: STEP,
          callback: (v) => `${v}h`,
          font: { size: 10 },
        },
        grid: { color: "#DDE2FF" },
      },
    },
  }; // [web:11]

  return (
    <aside className="sidegraph-container sidegraph-compact">
      <div className="sidegraph-header">
        <h3>3-Month Attendance Summary</h3>
        <div className="metrics">
          <span className="badge badge-blue">Checkouts: {checkoutCount}</span>
          <span className="badge badge-yellow">Short: {shortfallCount}</span>
          <span className="badge badge-red">Nocheckout: {missingCheckoutCount}</span>
          <span className="badge badge-darkred">No‑show: {noShowCount}</span>
        </div>
      </div>

      <div className="chart-wrap chart-compact">
        <Bar data={data} options={options} />
      </div>

      <div className="daily-list daily-compact">
        <h4>{monthKey(now)} Details</h4>
        <ul>
          {dailyStatuses.length === 0 ? (
            <li className="empty">No records</li>
          ) : (
            dailyStatuses.map((d) => {
              const sat = isSaturdayIST(d.date);
              const target = sat ? 6 : 9;
              const hours = d.totalHours ? d.totalHours.toFixed(2) : "0.00";
              let cls = "pill pill-gray";
              let label = "Absent";
              if (d.isCheckedOut) {
                const ok = d.totalHours + 1e-9 >= (sat ? 6 : 9);
                label = ok ? "Green" : "Yellow";
                cls = ok ? "pill pill-green" : "pill pill-yellow";
              } else if (d.isMissingOut) {
                cls = "pill pill-red"; label = "Warning";
              } else if (d.isNoShow) {
                cls = "pill pill-darkred"; label = "No-show";
              } else if (d.isPendingToday) {
                cls = "pill pill-gray"; label = "Pending";
              }
              return (
                <li key={d.date.toISOString()}>
                  <span className="date">{fmtDMY(d.date)}</span>
                  <span className="info">
                    {hours}h / {target}h {sat ? "(Sat)" : "(Mon–Fri)"}
                  </span>
                  <span className={cls}>{label}</span>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </aside>
  );
}
